import { Description, Inventory, LocalOffer, Store } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useLocation, useMatches, useNavigate, useParams } from 'react-router'
import styled from 'styled-components'

import { EmptyState } from 'components/empty-state/EmptyState'
import { CatalogFilterSidebar } from 'features/catalog/components/CatalogFilterSidebar/CatalogFilterSidebar'
import { CatalogYogiqlFilterSidebar } from 'features/catalog/components/CatalogYogiqlFilterSidebar/CatalogYogiqlFilterSidebar'
import { LENS_VIEW_TYPE } from 'features/catalog/constants'
import CatalogDetail from 'features/catalog/features/detail/Container'
import SyndicatedSourceDetail from 'features/catalog/features/syndicated-detail/Container'
import CatalogTable from 'features/catalog/features/table/Container'
import { updateQueryPanelDate } from 'features/catalog/hooks/catalog-yogiql-sidebar-handlers'
import { useCatalogFilterHash } from 'features/catalog/hooks/use-catalog-filter-hash'
import { useCatalogPanel } from 'features/catalog/hooks/use-catalog-panel'
import {
  useCatalogRouteSync,
  useCatalogUrlNavigation,
  useParsedCatalogUrlState,
} from 'features/catalog/hooks/use-catalog-routing'
import { useCatalogTableMetrics } from 'features/catalog/hooks/use-catalog-table-metrics'
import { useCatalogTableRows } from 'features/catalog/hooks/use-catalog-table-rows'
import { useCatalogYogiQLHistory } from 'features/catalog/hooks/use-catalog-yogiql-history'
import { useCatalogYogiQLPage } from 'features/catalog/hooks/use-catalog-yogiql-page'
import { useCatalogService } from 'features/catalog/service'
import { useCatalogFiltersStore } from 'features/catalog/store'
import type { FocusListOption } from 'features/catalog/types'
import { buildProjectCatalogPathFromLens } from 'features/catalog/utils'
// bully ignore[no-cross-feature-private-imports: the established v1<->v2 translator seam already used by use-charts-v2-state.ts, dashboards-v2, and catalog's own detail Container. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { translatePanelToV1Filters } from 'features/charts-v2/utils/translate-v2-to-v1-filters'
import { useEnhancedDialectFlag } from 'features/feedback/enhanced-dialect-flag'
// bully ignore[no-cross-feature-private-imports: the established cross-page carry-over primitive every v2-native page uses (Feedback/Keywords). follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { usePublishPanelsToGlobalFilters } from 'features/filters/hooks/use-publish-panels-to-global-filters'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { useNav } from 'features/project/partials/project-layout/use-layout'
import { useProjectStore } from 'features/project/store'
import {
  GLOBAL_SHORTCUT_KEYS,
  useGlobalShortcut,
  useUndoRedoShortcuts,
} from 'hooks/keyboard-shortcuts'
import { useSnapshotHistory } from 'hooks/use-snapshot-history'
import mixpanel from 'trackers/mixpanel'
import { ProjectState } from 'types/project'

const ICONS = {
  product: <Inventory style={{ fontSize: '32px' }} />,
  brand: <LocalOffer fontSize="large" />,
  source: <Store fontSize="large" />,
  theme: <Description fontSize="large" />,
}

// fyi this component used to be called Focus
const Catalog: React.FC = () => {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams<{
    brandName?: string
    productName?: string
    themeName?: string
    subThemeName?: string
    sourceName?: string
  }>()
  const matches = useMatches()
  const currentHandle = matches[matches.length - 1]?.handle as
    | { collectionType?: string }
    | undefined
  const collectionType = currentHandle?.collectionType
  const itemName =
    params.brandName ??
    params.productName ??
    params.themeName ??
    params.sourceName
  const subthemeName = params.subThemeName
  const parsedUrlState = useParsedCatalogUrlState(
    collectionType,
    itemName,
    subthemeName,
    location.search,
  )

  // Whether the URL selects a specific item (`/catalog/{lens}/{item}`) rather
  // than the bare list (`/catalog/{lens}`). Derived synchronously from the URL
  // (not the `selectedFocus` state below, which only syncs on a later effect
  // and would flicker for one render on navigation).
  const isDetailPage = !!parsedUrlState.selectedFocusFromUrl

  // Get layout context for nested sidebar
  const {
    setHasNestedSidebar,
    hasNestedSidebar,
    setIsNestedSidebarOpen,
    isNestedSidebarOpen,
    toggleNestedSidebar,
    portalContainer,
  } = useNav()

  // Register keyboard shortcut for sidebar toggle
  useGlobalShortcut(
    GLOBAL_SHORTCUT_KEYS.NESTED_SIDEBAR_TOGGLE,
    toggleNestedSidebar,
    {
      respectInputs: true,
      enabled: hasNestedSidebar,
    },
  )

  const [selectedLens, setSelectedLens] = useState<string | null>(
    'product_hierarchy',
  )
  const effectiveLens = selectedLens ?? 'product_hierarchy'

  const [groupCountries, setGroupCountries] = useState(true)
  const [groupThemes, setGroupThemes] = useState(true)

  // Catalog's v2 filter panel (the sidebar's query builder edits this). Carries
  // over from/to other pages via the shared v1 bridge — see
  // `use-catalog-filter-hash.ts` (inbound + URL persistence) and
  // `usePublishPanelsToGlobalFilters` below (outbound).
  // Under `enhanced_dialect` the panel is a YogiQL query; `panel` is its v2
  // reading for the page's v2 readers.
  const v2Panels = useCatalogFiltersStore((state) => state.panels)
  const v2UpdatePanel = useCatalogFiltersStore((state) => state.updatePanel)
  const activePanelId = useCatalogFiltersStore((state) => state.activePanelId)
  const ghostFilters = useCatalogFiltersStore((state) => state.ghostFilters)
  const restoreFilters = useCatalogFiltersStore((state) => state.restore)
  const catalogService = useCatalogService()
  const yogiqlFlag = useEnhancedDialectFlag()
  const yogiql = yogiqlFlag.enabled
  const yogiqlPage = useCatalogYogiQLPage(yogiqlFlag, projectId, catalogService)
  const panel = useCatalogPanel(
    yogiql,
    yogiqlPage.codec?.options ?? defaultFilterList,
  )
  const panels = useMemo(
    () => (yogiql ? [panel] : v2Panels),
    [yogiql, panel, v2Panels],
  )
  const updatePanel = yogiql ? updateQueryPanelDate : v2UpdatePanel
  const filtersSettled = useCatalogFilterHash(catalogService, yogiqlPage)
  usePublishPanelsToGlobalFilters(panels)

  // Undo/redo for the sidebar's filters and the date range — state the URL
  // writer only sometimes persists (date-only edits never reach `?filters=`),
  // so it is a snapshot history rather than a URL-hash one. Recording waits for
  // the URL hydration so a deep link's loaded filters aren't a phantom step.
  const v2History = useSnapshotHistory({
    key: { panels: v2Panels, ghostFilters },
    snapshot: () => ({ panels: v2Panels, activePanelId, ghostFilters }),
    restore: restoreFilters,
    enabled: !yogiql && !isDetailPage && filtersSettled,
  })
  const yogiqlHistory = useCatalogYogiQLHistory(
    yogiql && !isDetailPage && filtersSettled,
  )
  const history = yogiql ? yogiqlHistory : v2History
  useUndoRedoShortcuts({ ...history, enabled: !isDetailPage })

  const localFilters = useMemo(() => translatePanelToV1Filters(panel), [panel])

  // Row data for the current lens — owned here (not inside CatalogTable) so
  // both the table AND the detail-item resolution below read the same
  // unfiltered row set. See use-catalog-table-rows.ts's doc comment for how
  // rows are panel-filter-scoped vs. date-only-scoped.
  const { rows, isLoading: rowsLoading } = useCatalogTableRows({
    projectId,
    selectedLens: effectiveLens,
    panel,
    defaultFilterList,
    groupCountries,
    groupThemes,
  })

  // Sentences/Sentiment/Rating summary columns for the table — a second,
  // independent fetch (a `/charts/chart` group-by, not part of the row set
  // above). See use-catalog-table-metrics.ts's doc comment.
  const { metricsByValue, isLoading: metricsLoading } = useCatalogTableMetrics({
    projectId,
    selectedLens: effectiveLens,
    panel,
    enabled: !isDetailPage,
    groupThemes,
  })

  // One combined gate for the table's skeleton — every data source that
  // feeds a visible number (rows/Records, Sentences/Sentiment/Rating) must
  // resolve before the table renders, so switching View never shows a flash
  // of real rows with 0s while metrics are still in flight.
  const tableLoading = rowsLoading || metricsLoading

  const [selectedFocus, setSelectedFocus] = useState<string | null>(null)
  const { updateLens, updateFocus, updateSubtheme } = useCatalogUrlNavigation(
    projectId,
    selectedLens,
    selectedFocus,
    location.search,
    navigate,
  )

  // Navigates straight from a grouped theme row's expanded subtheme child to
  // its detail page, with the subtheme pre-selected. Distinct from
  // `updateSubtheme` above (which needs `selectedFocus` already set — the
  // detail page's own subtheme switcher): this fires from the LIST view,
  // before any item is selected, so it names the parent theme explicitly.
  const navigateToSubtheme = useCallback(
    (parentValue: string, subthemeValue: string, openNewTab?: boolean) => {
      if (!projectId) return
      const path = buildProjectCatalogPathFromLens(
        projectId,
        'theme',
        parentValue,
        subthemeValue,
      )
      if (openNewTab) {
        window.open(path, '_blank', 'noopener,noreferrer')
        return
      }
      navigate(path)
    },
    [projectId, navigate],
  )

  useEffect(() => {
    mixpanel.track('catalog page')
  }, [])

  // The filter sidebar only applies to the list view — its controls scope
  // what the list shows, which doesn't do anything on a detail page (that
  // page shows one item, already scoped by whatever the list's filters were
  // when it was opened).
  useEffect(() => {
    setHasNestedSidebar(!isDetailPage)
    setIsNestedSidebarOpen(!isDetailPage)

    return () => {
      setHasNestedSidebar(false)
      setIsNestedSidebarOpen(false)
    }
  }, [isDetailPage, setHasNestedSidebar, setIsNestedSidebarOpen])

  useCatalogRouteSync({
    projectId,
    collectionType,
    itemName,
    subthemeName,
    search: location.search,
    navigate,
    parsedUrlState,
    setSelectedLens,
    setSelectedFocus,
  })

  const focusItem = rows.find(
    (item) => item.value.toLowerCase() === (selectedFocus ?? '').toLowerCase(),
  )

  // A deep link to a country-specific source variant (e.g. "amazon · australia")
  // is absent from the row set when "Group countries" is on (the table then
  // carries only the grouped "Amazon" entry). Resolve it from that grouped entry
  // — which already holds the source identifiers + its countries — so the detail
  // pane loads regardless of the toggle state.
  const countryVariantItem = useMemo((): FocusListOption | undefined => {
    if (focusItem || !selectedFocus || selectedLens !== 'source_type') {
      return undefined
    }
    const sepIndex = selectedFocus.lastIndexOf(' · ')
    if (sepIndex === -1) return undefined
    const rawSource = selectedFocus.slice(0, sepIndex)
    const rawCountry = selectedFocus.slice(sepIndex + 3)
    const sourceEntry = rows.find(
      (item) => item.value.toLowerCase() === rawSource.toLowerCase(),
    )
    if (!sourceEntry) return undefined
    // Resolve the canonical-cased country from the source's countries list; if
    // it isn't there, leave country undefined so the detail falls back to the
    // source aggregate rather than mis-filtering on a wrong-cased value.
    const country = sourceEntry.countries?.find(
      (c) => c.toLowerCase() === rawCountry.toLowerCase(),
    )
    return {
      // Use canonical-cased parts so the title renders "Amazon · Australia",
      // matching the breadcrumb and the ungrouped table path.
      value: `${sourceEntry.value} · ${country ?? rawCountry}`,
      n_posts: sourceEntry.n_posts,
      identifiers: sourceEntry.identifiers,
      image_url: sourceEntry.image_url,
      sourceName: sourceEntry.value,
      country,
    }
  }, [focusItem, selectedFocus, selectedLens, rows])

  // Get the display name for the selected lens. Derived from a static map so
  // the word + icon are stable from first paint, rather than degrading to
  // 'item' (with no icon) while the async defaultFilterList is still loading.
  const selectedViewType = selectedLens
    ? (LENS_VIEW_TYPE[selectedLens] ?? 'item')
    : 'item'

  // An item is "intended" the moment the URL selects one — but its data (and so
  // the resolved focusItem) may not have loaded yet. In that window we still
  // render the real detail pane, in a loading state, so the layout is identical
  // to the loaded view rather than a blank/spinner. focusItem becomes truthy
  // once the row query resolves, flipping detailLoading off.
  const resolvedFocusItem = focusItem ?? countryVariantItem
  const detailLoading = !!selectedFocus && !resolvedFocusItem
  const detailItem: FocusListOption | undefined =
    resolvedFocusItem ??
    (selectedFocus
      ? { value: selectedFocus, n_posts: 0, identifiers: [] }
      : undefined)

  return (
    <Container>
      {/* Render the filter sidebar (a pure query builder) into the nested sidebar portal — list view only, see isDetailPage above */}
      {portalContainer &&
        !isDetailPage &&
        ReactDOM.createPortal(
          yogiql ? (
            <CatalogYogiqlFilterSidebar
              isCollapsed={!isNestedSidebarOpen}
              onToggleCollapse={toggleNestedSidebar}
              codec={yogiqlPage.codec}
            />
          ) : (
            <CatalogFilterSidebar
              isCollapsed={!isNestedSidebarOpen}
              onToggleCollapse={toggleNestedSidebar}
            />
          ),
          portalContainer,
        )}

      {/* Main content area — either the detail view, or the table when no item is selected */}
      {resolvedFocusItem?.syndicated ? (
        <SyndicatedSourceDetail
          item={resolvedFocusItem}
          projectId={projectId}
        />
      ) : detailItem && selectedLens && selectedFocus ? (
        <CatalogDetail
          key={`${selectedLens}_${selectedFocus}`}
          item={detailItem}
          isLoading={detailLoading}
          lens={selectedLens}
          setSubthemeValue={updateSubtheme}
          subthemeName={parsedUrlState.selectedSubthemeFromUrl}
          localFilters={localFilters}
        />
      ) : rows.length === 0 && !rowsLoading ? (
        <EmptyState
          icon={ICONS[selectedViewType.toLowerCase() as keyof typeof ICONS]}
          title={`Analyze a ${selectedViewType.toLowerCase()}`}
          description={`No ${selectedViewType.toLowerCase()}s match the current filters.`}
          textWidth="300px"
        />
      ) : (
        <CatalogTable
          projectId={projectId}
          selectedLens={effectiveLens}
          onSelectLens={updateLens}
          onSelectFocus={updateFocus}
          onSelectSubtheme={navigateToSubtheme}
          panel={panel}
          updatePanel={updatePanel}
          rows={rows}
          isLoading={tableLoading}
          metricsByValue={metricsByValue}
          groupCountries={groupCountries}
          onGroupCountriesChange={setGroupCountries}
          groupThemes={groupThemes}
          onGroupThemesChange={setGroupThemes}
          history={history}
        />
      )}
    </Container>
  )
}

export default Catalog

const Container = styled.div`
  height: 100%;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  display: flex;
  align-items: stretch;
  justify-content: flex-start;
`
